import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { getMasterCampaignBuildersAverageMonthlyBudget, getTotalMonthRange } from 'utils/CampaignMathUtils'

export const getPlanDescription = (accountName: string) => {
  const firstParagraph = `This comprehensive marketing plan for ${accountName} has been meticulously crafted to maximize reach and engagement across your target demographic. Our strategic approach combines in-depth market research, competitive analysis, and consumer behavior insights to create a data-driven foundation for your advertising campaigns. The plan includes detailed audience segmentation, optimal budget allocation recommendations, and precise targeting strategies designed to deliver measurable results and strong return on investment.`

  const secondParagraph = `Each campaign component has been carefully selected and optimized based on your specific business objectives, geographic markets, and customer personas. The implementation timeline includes detailed milestone tracking, performance metrics, and adaptive strategies to ensure campaign success throughout the entire execution period. This holistic approach ensures maximum market penetration while maintaining cost efficiency and brand consistency across all touchpoints.`

  return {
    firstParagraph,
    secondParagraph,
    combined: `${firstParagraph}\n\n${secondParagraph}`
  }
}

export const getChannelUtilizationContent = (campaigns: MasterCampaignPlanCampaignBuilder[]): string | null => {
  const usedTypes = new Set(campaigns.map(campaign => campaign.campaignType))
  const hasTV = usedTypes.has(MasterCampaignPlanCampaignBuilderType.TV)
  const hasInternet = usedTypes.has(MasterCampaignPlanCampaignBuilderType.Internet)
  const hasBillboard = usedTypes.has(MasterCampaignPlanCampaignBuilderType.Billboard)

  let obj

  if (hasTV && hasInternet && hasBillboard) {
    obj = {
      planDescription: "Seeing related ads across different channel formats significantly increases their memorability. In fact, Nielsen's CrossPlatform Advertising Report indicates that advertising using three or more integrated channels results in a 35% higher ROI than single-channel efforts. As such, this plan uses Connected TV (CTV), Programmatic Internet Ads and Digital Roadside Billboards.",
      content:
        'CTV is particularly effective at both communicating your entire message and raising the credibility level of your brand. While it is more expensive than the other formats on a CPM basis, it will be tightly targeted to your intended audience for maximum impact.\n\nInternet ads are less credibility-building than CTV or Billboards, but they are both highly targetable and low cost, making them ideal for keeping your brand top-of-mind. We can use these internet ads for website retargeting (those who have visited your website) and CTV retargeting (those who have seen your CTV commercial). Optionally, we can also target these ads to any custom audience or first-party data list you provide.\n\nBillboards lack the targeting controls of CTV and Internet, but they add far more reach. This makes them incredibly effective at supporting brand awareness across an entire geographic area. Here we will use them to keep you top-of-mind for those who have seen your targeted advertising and to build awareness for those who have not.'
    }
  } else if (hasTV && hasInternet) {
    obj = {
      planDescription: 'This plan uses Connected TV (CTV) and Programmatic Internet Ads.',
      content:
        'CTV is particularly effective at both communicating your entire message and raising the credibility level of your brand. While it is more expensive than other formats on a CPM basis, it will be tightly targeted to your intended audience for maximum impact.\n\nInternet ads are less credibility-building than CTV, but they are both highly targetable and low cost, making them ideal for supporting your CTV campaign and keeping your brand top-of-mind. We can use these internet ads for website retargeting (those who have visited your website) and CTV retargeting (those who have seen your CTV commercial). Optionally, we can also target these ads to any custom audience or first-party data list you provide.'
    }
  } else if (hasTV && hasBillboard) {
    obj = {
      planDescription: 'This plan uses Connected TV (CTV) and Digital Roadside Billboards.',
      content:
        'CTV is particularly effective at both communicating your entire message and raising the credibility level of your brand. While it is more expensive than other formats on a CPM basis, it will be tightly targeted to your intended audience for maximum impact.\n\nBillboards lack the targeting controls of CTV, but they add far more reach. This makes them incredibly effective at supporting brand awareness across an entire geographic area. Here we will use them to keep you top-of-mind for those who have seen your CTV advertising and to build awareness for those who have not.'
    }
  } else if (hasInternet && hasBillboard) {
    obj = {
      planDescription: 'This plan uses Programmatic Internet Ads and Digital Roadside Billboards.',
      content:
        'Internet ads are both highly targetable and low cost, making them ideal for keeping your brand top-of-mind. We can use these internet ads for website retargeting (those who have visited your website) and CTV retargeting (those who have seen your CTV commercial). Optionally, we can also target these ads to any custom audience or first-party data list you provide.\n\nBillboards lack the targeting controls of internet ads, but they add far more reach. This makes them incredibly effective at building credibility and supporting brand awareness across an entire geographic area. Here we will use them to keep you top-of-mind for those who have seen your targeted advertising and to build awareness for those who have not.'
    }
  }

  return obj ? `${obj.planDescription}\n\n${obj.content}` : null
}

export const getBudgetContent = (campaigns: MasterCampaignPlanCampaignBuilder[], useMediaPlanWording: boolean = false): string => {
  const durationMonths = getTotalMonthRange(campaigns)
  const averageMonthlySpend = Math.round(getMasterCampaignBuildersAverageMonthlyBudget(durationMonths, campaigns))

  interface ChannelKey {
    type: MasterCampaignPlanCampaignBuilderType
    offAdCritterName?: string
  }

  const isValidCampaignType = (campaign: MasterCampaignPlanCampaignBuilder): boolean => {
    if (campaign.isOffAdCritter && campaign.offAdCritterName) {
      return true
    }

    return campaign.campaignType === MasterCampaignPlanCampaignBuilderType.TV || campaign.campaignType === MasterCampaignPlanCampaignBuilderType.Internet || campaign.campaignType === MasterCampaignPlanCampaignBuilderType.Billboard
  }

  const channelBudgets = new Map<string, { key: ChannelKey; budget: number }>()
  let totalBudget = 0

  campaigns.forEach(campaign => {
    if (!isValidCampaignType(campaign)) {
      return
    }

    const channelId = campaign.isOffAdCritter && campaign.offAdCritterName ? `offadcritter_${campaign.offAdCritterName}` : campaign.campaignType

    const existing = channelBudgets.get(channelId)
    if (existing) {
      existing.budget += campaign.intervalBudget
    } else {
      channelBudgets.set(channelId, {
        key: {
          type: campaign.campaignType,
          offAdCritterName: campaign.offAdCritterName
        },
        budget: campaign.intervalBudget
      })
    }
    totalBudget += campaign.intervalBudget
  })

  const channelPercentages = new Map<string, number>()
  if (totalBudget > 0) {
    channelBudgets.forEach((value, channelId) => {
      const percentage = (value.budget / totalBudget) * 100
      channelPercentages.set(channelId, percentage)
    })
  }

  const formatPercentage = (percentage: number): string => {
    const rounded = Math.round(percentage)
    return rounded < 1 ? '<1' : rounded.toString()
  }

  const socialOrder = ['Google', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok']
  const customOffAdCritterOrder: string[] = []

  campaigns.forEach(campaign => {
    if (campaign.isOffAdCritter && campaign.offAdCritterName) {
      if (!socialOrder.includes(campaign.offAdCritterName) && !customOffAdCritterOrder.includes(campaign.offAdCritterName)) {
        customOffAdCritterOrder.push(campaign.offAdCritterName)
      }
    }
  })

  const usedChannels = Array.from(channelBudgets.entries())
    .sort(([keyA, valueA], [keyB, valueB]) => {
      const getOrderWeight = (channelId: string, key: ChannelKey): number => {
        if (key.type === MasterCampaignPlanCampaignBuilderType.TV) return 0
        if (key.type === MasterCampaignPlanCampaignBuilderType.Internet) return 1

        if (key.offAdCritterName) {
          const socialIndex = socialOrder.indexOf(key.offAdCritterName)
          if (socialIndex !== -1) return 2 + socialIndex
        }

        if (key.type === MasterCampaignPlanCampaignBuilderType.Billboard) return 10

        if (key.offAdCritterName) {
          const customIndex = customOffAdCritterOrder.indexOf(key.offAdCritterName)
          if (customIndex !== -1) return 20 + customIndex
        }

        return 1000
      }

      return getOrderWeight(keyA, valueA.key) - getOrderWeight(keyB, valueB.key)
    })
    .map(([channelId]) => channelId)

  const getChannelDisplayName = (channelId: string, key: ChannelKey): string | null => {
    if (key.offAdCritterName) {
      return key.offAdCritterName
    }

    switch (key.type) {
      case MasterCampaignPlanCampaignBuilderType.TV:
        return 'CTV'
      case MasterCampaignPlanCampaignBuilderType.Internet:
        return 'internet ads'
      case MasterCampaignPlanCampaignBuilderType.Billboard:
        return 'digital billboards'
      default:
        return null
    }
  }

  let firstSentence = ''
  let secondSentence = ''
  let thirdSentence = ''

  const planType = useMediaPlanWording ? 'media plan' : 'marketing plan'

  if (durationMonths < 2) {
    secondSentence = `The monthly budget is $${averageMonthlySpend.toLocaleString()} per month.`
  } else {
    firstSentence = `This ${planType} spans ${durationMonths} month${durationMonths === 1 ? '' : 's'}. `
    secondSentence = `While the exact amount varies, the average monthly spend across this time period is $${averageMonthlySpend.toLocaleString()} per month.`
  }

  if (usedChannels.length > 1) {
    const validChannels = usedChannels.filter(channelId => {
      const channelData = channelBudgets.get(channelId)
      return channelData && getChannelDisplayName(channelId, channelData.key) !== null
    })

    if (validChannels.length === 0) {
      return `${firstSentence}${secondSentence}`
    }

    if (validChannels.length === 1) {
      const channelId = validChannels[0]
      const channelData = channelBudgets.get(channelId)!
      const displayName = getChannelDisplayName(channelId, channelData.key)
      thirdSentence = ` The budget is allocated to ${displayName}.`
    } else if (validChannels.length === 2) {
      const [firstChannelId, secondChannelId] = validChannels
      const firstChannelData = channelBudgets.get(firstChannelId)!
      const secondChannelData = channelBudgets.get(secondChannelId)!
      const firstName = getChannelDisplayName(firstChannelId, firstChannelData.key)
      const secondName = getChannelDisplayName(secondChannelId, secondChannelData.key)
      const firstPercentage = formatPercentage(channelPercentages.get(firstChannelId) || 0)
      const secondPercentage = formatPercentage(channelPercentages.get(secondChannelId) || 0)
      thirdSentence = ` Approximately ${firstPercentage}% of the budget is allocated to ${firstName} and ${secondPercentage}% to ${secondName}.`
    } else {
      const [firstChannelId, ...restChannelIds] = validChannels
      const firstChannelData = channelBudgets.get(firstChannelId)!
      const firstName = getChannelDisplayName(firstChannelId, firstChannelData.key)
      const firstPercentage = formatPercentage(channelPercentages.get(firstChannelId) || 0)

      const restStrings = restChannelIds.slice(0, -1).map(channelId => {
        const channelData = channelBudgets.get(channelId)!
        const displayName = getChannelDisplayName(channelId, channelData.key)
        const percentage = formatPercentage(channelPercentages.get(channelId) || 0)
        return `${percentage}% to ${displayName}`
      })

      const lastChannelId = restChannelIds[restChannelIds.length - 1]
      const lastChannelData = channelBudgets.get(lastChannelId)!
      const lastName = getChannelDisplayName(lastChannelId, lastChannelData.key)
      const lastPercentage = formatPercentage(channelPercentages.get(lastChannelId) || 0)

      thirdSentence = ` Approximately ${firstPercentage}% of the budget is allocated to ${firstName}, ${restStrings.join(', ')} and ${lastPercentage}% to ${lastName}.`
    }
  }

  return `${firstSentence}${secondSentence}${thirdSentence}`
}
